/**
 * Panel Types for Workspace-Centric Architecture
 * 
 * Defines types for page panels that appear as tabs alongside documents
 * in the Aerotage Workspace interface.
 */

import type { AeroDocument } from './document-schema';
import type { PlanDocument } from './plan-document';

// Panel/Tab type discriminator
export type TabType = 'document' | 'plan' | 'page' | 'diagram' | 'mermaid' | 'coding';

// Mermaid diagram type (matches backend MermaidDiagramType)
export type MermaidDiagramType = 
  | 'flowchart' | 'sequence' | 'state' | 'er' | 'graph'
  | 'classDiagram' | 'gantt' | 'pie' | 'timeline'
  | 'mindmap' | 'quadrantChart' | 'requirement' | 'c4Context';

// Page panel identifiers
export type PagePanelId = 
  | 'reference-library'
  | 'projects'
  | 'company-docs'
  | 'doc-control'
  | 'quality'
  | 'validation'
  | 'crosswalk'
  | 'suppliers'
  | 'reliability-admin' // Reliability data sources admin
  | 'users'           // Admin user management
  | 'my-profile'      // User profile settings
  | 'preferences'     // Theme, notifications
  | 'organization'    // Organization settings (admin)
  | 'billing';        // Billing (admin)

// Reference document scope types for grouping (external technical references only)
// Note: "company-reference" removed - company docs belong in Document Control
export type ReferenceScopeType = 
  | 'industry-standard'
  | 'advisory-circular'
  | 'guidance'
  | 'specification';

// Configuration for each panel type
export interface PagePanelConfig {
  id: PagePanelId;
  title: string;
  icon: string;
  supportsUpload: boolean;
  supportsSearch: boolean;
  searchPlaceholder?: string;
}

// Page panel configurations
export const PAGE_PANEL_CONFIGS: Record<PagePanelId, PagePanelConfig> = {
  'reference-library': {
    id: 'reference-library',
    title: 'Reference Library',
    icon: '📚',
    supportsUpload: true,
    supportsSearch: true,
    searchPlaceholder: 'Search standards, guidance, specifications...',
  },
  'projects': {
    id: 'projects',
    title: 'Projects',
    icon: '📁',
    supportsUpload: false,
    supportsSearch: true,
    searchPlaceholder: 'Search projects...',
  },
  'company-docs': {
    id: 'company-docs',
    title: 'Company Documents',
    icon: '🏢',
    supportsUpload: true,
    supportsSearch: true,
    searchPlaceholder: 'Search policies, procedures, templates...',
  },
  'doc-control': {
    id: 'doc-control',
    title: 'Document Control',
    icon: '📂',
    supportsUpload: false,
    supportsSearch: true,
    searchPlaceholder: 'Search documents...',
  },
  'quality': {
    id: 'quality',
    title: 'Quality',
    icon: '📊',
    supportsUpload: false,
    supportsSearch: false,
  },
  'validation': {
    id: 'validation',
    title: 'Validation',
    icon: '✓',
    supportsUpload: false,
    supportsSearch: false,
  },
  'crosswalk': {
    id: 'crosswalk',
    title: 'Crosswalk',
    icon: '🔀',
    supportsUpload: false,
    supportsSearch: true,
    searchPlaceholder: 'Search requirements...',
  },
  'suppliers': {
    id: 'suppliers',
    title: 'Suppliers',
    icon: '🏭',
    supportsUpload: false,
    supportsSearch: true,
    searchPlaceholder: 'Search suppliers...',
  },
  'reliability-admin': {
    id: 'reliability-admin',
    title: 'Reliability Data',
    icon: '📈',
    supportsUpload: true,
    supportsSearch: true,
    searchPlaceholder: 'Search failure rates, data sources...',
  },
  'users': {
    id: 'users',
    title: 'Users',
    icon: '👥',
    supportsUpload: false,
    supportsSearch: true,
    searchPlaceholder: 'Search users...',
  },
  'my-profile': {
    id: 'my-profile',
    title: 'My Profile',
    icon: '👤',
    supportsUpload: false,
    supportsSearch: false,
  },
  'preferences': {
    id: 'preferences',
    title: 'Preferences',
    icon: '⚙️',
    supportsUpload: false,
    supportsSearch: false,
  },
  'organization': {
    id: 'organization',
    title: 'Organization',
    icon: '🏢',
    supportsUpload: false,
    supportsSearch: false,
  },
  'billing': {
    id: 'billing',
    title: 'Billing',
    icon: '💳',
    supportsUpload: false,
    supportsSearch: false,
  },
};

// Unified tab interface for all tab types
export interface OpenedTab {
  // Common fields for all tab types
  id: string;
  tabId: number;
  title: string;
  type: TabType;
  
  // Document-specific fields (when type === 'document')
  documentNumber?: string;
  projectId?: string;
  canEdit?: boolean;
  jsonDocument?: AeroDocument;
  version?: string;
  
  // Page panel-specific fields (when type === 'page')
  panelType?: PagePanelId;
  searchQuery?: string;
  
  // Plan-specific fields (when type === 'plan')
  planDocument?: PlanDocument;
  
  // Diagram-specific fields (when type === 'diagram')
  diagramId?: string;
  
  // Mermaid/Flow diagram-specific fields (when type === 'mermaid')
  mermaidDiagramId?: string;
  mermaidType?: MermaidDiagramType;
  svgUrl?: string;
}

// Reference document with AI-generated metadata
export interface ReferenceDocumentCard {
  documentId: string;
  title: string;
  documentType: ReferenceScopeType;
  standardNumber?: string;
  description?: string;
  
  // AI-generated content (stored at upload time)
  aiSummary?: string;
  summaryGeneratedAt?: string;
  
  // Indexing status
  indexStatus: 'pending' | 'processing' | 'batch_processing' | 'password_required' | 'completed' | 'failed';
  chunkCount?: number;
  errorMessage?: string;
  
  // Metadata
  organization?: string;
  version?: string;
  publishDate?: string;
  revisionDate?: string;
  tags?: string[];
  createdAt: string;
  lastAccessedAt?: string;
  
  // File info
  fileFormat?: string;  // pdf, docx, xlsx, txt
  
  // Project assignment - empty/undefined means "All Projects" for this customer
  // Enriched by backend with project names/codes
  assignedProjects?: { projectId: string; projectName: string; projectCode?: string }[];
  
  // Neptune relationships (clickable tags)
  relatedDocuments?: RelatedDocumentTag[];
  
  // Search relevance (when from search results)
  relevanceScore?: number;
  matchedPassage?: string;
}

// Related document tag (from Neptune graph)
export interface RelatedDocumentTag {
  documentId: string;
  title: string;
  standardNumber?: string;
  relationshipType: 'references' | 'related_to' | 'supersedes' | 'supplements';
}

// Upload review state for AI-vision extraction
export interface UploadReviewState {
  file: File;
  thumbnail?: string;
  status: 'uploading' | 'extracting' | 'review' | 'saving' | 'complete' | 'error';
  error?: string;
  
  // AI-extracted metadata
  extractedMetadata?: {
    title: string;
    documentNumber?: string;
    version?: string;
    publishDate?: string;
    organization?: string;
    documentType: ReferenceScopeType;
    aiSummary: string;
    suggestedRelatedDocs?: string[];
    
    // Confidence scores per field (0-1)
    confidence: {
      title: number;
      documentNumber: number;
      version: number;
      publishDate: number;
      organization: number;
      documentType: number;
    };
  };
}

// Recent documents grouped by scope type
export interface RecentDocumentGroup {
  scopeType: ReferenceScopeType;
  label: string;
  icon: string;
  documents: ReferenceDocumentCard[];
  isExpanded: boolean;
}

// Helper to get scope type display info
export const SCOPE_TYPE_INFO: Record<ReferenceScopeType, { label: string; icon: string }> = {
  'industry-standard': { label: 'Industry Standards', icon: '📋' },
  'advisory-circular': { label: 'Advisory Circulars', icon: '📜' },
  'guidance': { label: 'Guidance Documents', icon: '📖' },
  'specification': { label: 'Specifications', icon: '📐' },
};

// Helper to convert route path to panel ID
export function pathToPanelId(path: string): PagePanelId | null {
  const mapping: Record<string, PagePanelId> = {
    '/reference-library': 'reference-library',
    '/projects': 'projects',
    '/company-documents': 'company-docs',
    '/document-control': 'doc-control',
    '/quality': 'quality',
    '/validation': 'validation',
    '/crosswalk': 'crosswalk',
    '/suppliers': 'suppliers',
    '/reliability-data': 'reliability-admin',
    '/users': 'users',
    '/my-profile': 'my-profile',
    '/preferences': 'preferences',
    '/organization': 'organization',
    '/billing': 'billing',
  };
  return mapping[path] || null;
}

// Helper to check if a route should open as a panel
export function isPanelRoute(path: string): boolean {
  return pathToPanelId(path) !== null;
}
